import './kore-navigation.js?v=1.3.1';

/* Native scroll, native Shopify forms. No third-party tracking or simulated submissions. */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');

class KoreHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('.kore-hero-carousel__track');
    this.dots = [...this.querySelectorAll('[data-slide]')];
    this.controls = this.querySelector('.kore-hero-carousel__dots');
    if (!this.track || !this.dots.length) return;
    this.index = 0; this.visible = false; this.hovering = false; this.focused = false;
    this.stopped = this.dataset.autoplay !== 'true';
    this.controls.hidden = false;
    this.playback = this.querySelector('[data-playback]');
    if (!this.playback && this.dataset.autoplay === 'true') {
      this.playback = document.createElement('button');
      this.playback.type = 'button'; this.playback.dataset.playback = '';
      this.playback.innerHTML = '<svg viewBox="0 0 12 12" aria-hidden="true"><path class="kore-hero-pause" d="M2 1h3v10H2zM7 1h3v10H7z"/><path class="kore-hero-play" d="M3 1v10l8-5z"/></svg>';
      this.controls.append(this.playback);
    }
    this.update = () => {
      const width = this.track.clientWidth;
      if (width) this.index = Math.min(this.dots.length - 1, Math.round(this.track.scrollLeft / width));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      if (this.playback) {
        this.playback.hidden = reduce.matches;
        this.playback.setAttribute('aria-pressed', String(this.stopped || reduce.matches));
        this.playback.setAttribute('aria-label', this.stopped || reduce.matches ? 'Reproducir carrusel' : 'Pausar carrusel');
      }
    };
    this.schedule = () => {
      clearTimeout(this.timer);
      if (!this.visible || this.stopped || reduce.matches || this.hovering || this.focused || document.hidden) return;
      this.timer = setTimeout(() => { this.go((this.index + 1) % this.dots.length); this.schedule(); }, 5200);
    };
    this.go = index => {
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduce.matches ? 'instant' : 'smooth' });
    };
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => {
      this.stopped = true; this.go(i); this.update(); this.schedule();
    }, { signal }));
    this.playback?.addEventListener('click', () => {
      this.stopped = !this.stopped; this.update(); this.schedule();
    }, { signal });
    this.track.addEventListener('scroll', this.update, { passive:true, signal });
    this.track.addEventListener('pointerdown', () => { this.stopped = true; this.update(); this.schedule(); }, { passive:true, signal });
    this.track.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault(); this.stopped = true;
      let index = this.index + (event.key === 'ArrowRight' ? 1 : -1);
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = this.dots.length - 1;
      this.go(Math.min(this.dots.length-1,Math.max(0,index))); this.schedule(); this.update();
    }, { signal });
    this.addEventListener('pointerenter', event => { if (event.pointerType==='mouse') { this.hovering=true; this.schedule(); } }, { signal });
    this.addEventListener('pointerleave', () => { this.hovering=false; this.schedule(); }, { signal });
    this.addEventListener('focusin', () => { this.focused=true; this.schedule(); }, { signal });
    this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused=false; this.schedule(); } }, { signal });
    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); });
    this.observer.observe(this);
    this.resize = new ResizeObserver(() => { this.track.scrollLeft = this.index * this.track.clientWidth; this.update(); });
    this.resize.observe(this.track);
    document.addEventListener('visibilitychange', this.schedule, { signal });
    reduce.addEventListener('change', () => { this.update(); this.schedule(); }, { signal });
    this.update();
  }
  disconnectedCallback() {
    this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); clearTimeout(this.timer);
  }
}

class KoreLeadPreferences extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.abort = new AbortController();
    const { signal } = this.abort;
    const form = this.closest('form');
    if (!form) return;
    if (this.dataset.formKind === 'customer') {
      const tags = form.querySelector('input[name="contact[tags]"]');
      if (!tags) return;
      const base = (tags.dataset.baseTags || tags.value).split(',').map(t => t.trim()).filter(Boolean);
      const sync = () => {
        const selected = [...this.querySelectorAll('input[data-goal-tag]:checked')].map(input => input.dataset.goalTag);
        tags.value = [...new Set([...base, ...selected])].join(',');
      };
      this.addEventListener('change', sync, { signal });
      form.addEventListener('submit', sync, { signal });
      form.addEventListener('reset', () => queueMicrotask(sync), { signal });
      this.hidden = false; sync();
    }
  }
  disconnectedCallback() { this.abort?.abort(); }
}

class KoreClientReveal extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback(); this.animations = new Set();
    if (reduce.matches || this.closest('[data-motion="false"]')) return;
    this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      this.observer.unobserve(entry.target);
      const animation = entry.target.animate([{opacity:0,transform:'translateY(22px)'},{opacity:1,transform:'translateY(0)'}],{duration:650,easing:'cubic-bezier(.22,.61,.36,1)',fill:'backwards'});
      this.animations.add(animation); animation.onfinish=()=>this.animations.delete(animation);
    }), { threshold:.15 });
    this.querySelectorAll('[data-client-reveal]').forEach(node => this.observer.observe(node));
    this.change=()=>{if(reduce.matches){this.observer?.disconnect();this.animations.forEach(a=>a.cancel());}};
    reduce.addEventListener('change',this.change);
  }
  disconnectedCallback() { this.observer?.disconnect();this.animations?.forEach(a=>a.cancel());if(this.change)reduce.removeEventListener('change',this.change); }
}

if (!customElements.get('kore-hero-carousel')) customElements.define('kore-hero-carousel',KoreHeroCarousel);
if (!customElements.get('kore-lead-preferences')) customElements.define('kore-lead-preferences',KoreLeadPreferences);
if (!customElements.get('kore-client-reveal')) customElements.define('kore-client-reveal',KoreClientReveal);
